import { INCIDENT_NATURE_LABELS, reportCategoryOptions } from './occurrence.labels';
import {
  ComplaintReason, IncidentNature, OccurrenceNature, OccurrenceScope, OccurrenceSubScope, OccurrenceType, ReportCategory,
  ReporterNature, ReporterNatureDetail,
} from './occurrence.models';

/**
 * Tradução do catálogo normativo para a linguagem de quem participa pelo site.
 *
 * Quem entra no portal não sabe o que é RGPC, NP 4590 ou "gestão de comunicações" — sabe que quer
 * reclamar, sugerir ou denunciar. Cada motivo aqui mapeia para uma combinação válida de
 * âmbito/subâmbito/tipo/natureza do catálogo (ver `occurrence-catalog.ts`), e a equipa pode
 * reclassificar na triagem — a mudança fica registada no histórico da ocorrência.
 */
export interface PublicReason {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly icon: string;
  readonly scope: OccurrenceScope;
  /** Obrigatório no âmbito Outro: é o que o diz por extenso em vez de 'Outro'. */
  readonly scopeOther?: string;
  /** Ausente nos âmbitos que não têm subâmbitos (Outro, Segurança da Informação). */
  readonly subScope?: OccurrenceSubScope;
  readonly type: OccurrenceType;
  /** Natureza da participação — o que o modelo GBT usa para separar os canais. */
  readonly nature?: OccurrenceNature;
  /** Denúncias: mostra o aviso de tratamento reservado. */
  readonly sensitive?: boolean;
  /**
    * Motivos que o backend recusa em anónimo: exercício de direitos, sugestões/ideias e as
    * comunicações de proteção de dados.
    */
  readonly requiresIdentification?: boolean;
  /** Segundo nível: o participante escolhe o direito a exercer. */
  readonly rightsChoice?: boolean;
  /** Segundo nível: o participante escolhe o motivo da reclamação. */
  readonly complaintChoice?: boolean;
  /** Motivo fixo, quando o próprio motivo do portal já o determina. */
  readonly complaintReason?: ComplaintReason;
  /** Segundo nível: o participante escolhe a natureza da denúncia (o servidor classifica). */
  readonly natureChoice?: boolean;
  /** Segundo nível: o participante escolhe as categorias da denúncia. */
  readonly categoryChoice?: boolean;
  /** Categorias fixas, quando o motivo do portal já as determina. */
  readonly reportCategories?: readonly ReportCategory[];
  /** Fora da página inicial do portal: só se chega pelo endereço (ex.: o canal de denúncias). */
  readonly hiddenFromHome?: boolean;
}

export const PUBLIC_REASONS: readonly PublicReason[] = [
  {
    id: 'reclamacao',
    label: 'Fazer uma reclamação',
    description: 'Algo correu mal e quer que a empresa trate do assunto.',
    icon: 'fe-thumbs-down',
    scope: 'SISTEMAS_GESTAO', subScope: 'ISO_9001', type: 'PARTICIPACAO_COMUNICACAO',
    nature: 'RECLAMACAO',
    complaintChoice: true,
  },
  {
    // A folha dos campos do canal encaminha sugestões/ideias e elogios para o âmbito Outro: não
    // nascem na ISO 9001, que é o sistema das reclamações.
    id: 'sugestao',
    label: 'Dar uma sugestão ou ideia',
    description: 'Uma ideia para melhorar um serviço, um processo ou um novo desafio.',
    icon: 'fe-zap',
    scope: 'OUTRO', scopeOther: 'Sugestões e ideias', type: 'PARTICIPACAO_COMUNICACAO',
    nature: 'SUGESTAO_IDEIA',
    // Sugestões e ideias são sempre identificadas: há um contributo a reconhecer.
    requiresIdentification: true,
  },
  {
    id: 'elogio',
    label: 'Deixe o seu elogio/reconhecimento',
    description: 'Reconhecer um bom atendimento ou trabalho.',
    icon: 'fe-star',
    scope: 'OUTRO', scopeOther: 'Elogios e reconhecimentos', type: 'PARTICIPACAO_COMUNICACAO',
    nature: 'ELOGIO_RECONHECIMENTO',
  },
  {
    id: 'denuncia',
    label: 'Denunciar uma irregularidade',
    description: 'Uma prática que considera irregular ou ilegal.',
    icon: 'fe-shield',
    scope: 'RGPC', subScope: 'GESTAO_COMUNICACOES', type: 'PARTICIPACAO_COMUNICACAO',
    nature: 'DENUNCIA',
    natureChoice: true,
    sensitive: true,
    // A denúncia entra pelo canal de denúncias (`/canal-denuncias`), não pela página inicial.
    hiddenFromHome: true,
  },
  {
    id: 'bem-estar',
    label: 'Bem-estar no trabalho',
    description: 'Assédio, discriminação ou outra situação que afeta as pessoas.',
    icon: 'fe-heart',
    scope: 'SISTEMAS_GESTAO', subScope: 'NP_4590', type: 'PARTICIPACAO_COMUNICACAO',
    nature: 'RECLAMACAO',
    complaintReason: 'CONDICOES_TRABALHO_BEM_ESTAR',
    sensitive: true,
    // O bem-estar passou a natureza da denúncia (documento de 22/10).
    hiddenFromHome: true,
  },
  {
    id: 'dados-uso',
    label: 'Uso indevido de dados pessoais',
    description: 'Suspeita de que os seus dados foram tratados sem autorização.',
    icon: 'fe-lock',
    scope: 'RGPD', subScope: 'CONFORMIDADE_PROTECAO_DADOS', type: 'PARTICIPACAO_COMUNICACAO',
    nature: 'DENUNCIA',
    // O RGPD tem a sua lista de categorias desde 2026-10-01 e quem comunica escolhe nela.
    categoryChoice: true,
    // Sem identificação o caso não se pode relacionar com o titular dos dados.
    requiresIdentification: true,
    sensitive: true,
  },
  {
    id: 'dados-direitos',
    label: 'Exercer direitos sobre os meus dados',
    description: 'Aceder, corrigir, apagar ou opor-se ao uso dos seus dados.',
    icon: 'fe-user-check',
    scope: 'RGPD', subScope: 'EXERCICIO_DIREITOS', type: 'ACESSO',
    requiresIdentification: true,
    rightsChoice: true,
  },
];

/** Direitos do titular (segundo nível do motivo "exercer direitos"). */
export const RIGHTS_TYPES: readonly OccurrenceType[] = [
  'ACESSO', 'RETIFICACAO', 'APAGAMENTO', 'LIMITACAO_TRATAMENTO', 'PORTABILIDADE', 'OPOSICAO',
  'RETIRADA_CONSENTIMENTO', 'NAO_SUJEICAO_DECISAO_AUTOMATIZADA',
];

/** Naturezas da denúncia no portal: rótulo e descrição como aprovados (espelham o catálogo da API). */
export const REPORTER_NATURE_OPTIONS: readonly { value: ReporterNature; label: string }[] = [
  { value: 'BEM_ESTAR_CONDICOES_TRABALHO', label: 'Bem-Estar e condições de trabalho (Ambiente de trabalho, bem-estar e saúde)' },
  { value: 'ETICA_INTEGRIDADE_CONDUTA', label: 'Ética, integridade e conduta (Condutas irregulares e incumprimentos éticos)' },
  { value: 'SEGURANCA_INFORMACAO', label: 'Segurança da informação/Cibersegurança (Incidentes de segurança e proteção da informação)' },
  { value: 'PROTECAO_DADOS', label: 'Proteção de dados pessoais (RGPD) (Tratamento indevido de dados pessoais)' },
  { value: 'OUTRAS_INFRACOES', label: 'Outras infrações e irregularidades (Infrações legais e regulamentares noutros domínios)' },
];

export const REPORTER_NATURE_DETAIL_OPTIONS: readonly { value: ReporterNatureDetail; label: string }[] = [
  { value: 'DEFESA_CONSUMIDOR', label: 'Defesa do consumidor e incumprimento legal ou regulamentar de produtos e serviços' },
  { value: 'CONTRATACAO_PUBLICA_CONCORRENCIA', label: 'Contratação pública e concorrência' },
  { value: 'PROTECAO_AMBIENTE', label: 'Proteção do ambiente' },
  { value: 'OUTRA_IRREGULARIDADE', label: 'Outra irregularidade' },
];

/**
 * Seleção múltipla que cada natureza mostra por baixo do dropdown: as mesmas opções (e textos) do
 * formulário interno. A segurança da informação não usa categorias de denúncia mas a natureza do
 * incidente; "Outras infrações" não tem opções (só a sub-opção).
 */
export interface NatureChoices {
  readonly field: 'reportCategories' | 'incidentNatures';
  readonly options: readonly { value: string; label: string }[];
}

export const NATURE_CHOICES: Partial<Record<ReporterNature, NatureChoices>> = {
  BEM_ESTAR_CONDICOES_TRABALHO: { field: 'reportCategories', options: reportCategoryOptions('SISTEMAS_GESTAO') },
  ETICA_INTEGRIDADE_CONDUTA: { field: 'reportCategories', options: reportCategoryOptions('RGPC') },
  PROTECAO_DADOS: { field: 'reportCategories', options: reportCategoryOptions('RGPD') },
  SEGURANCA_INFORMACAO: {
    field: 'incidentNatures',
    options: (Object.keys(INCIDENT_NATURE_LABELS) as IncidentNature[])
      .map((value) => ({ value, label: INCIDENT_NATURE_LABELS[value] })),
  },
};
